import { Component, OnInit } from '@angular/core';
import { cloneDeep } from 'lodash';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Coordinate } from 'ol/coordinate';
import { NavaidModel } from 'src/app/models/navaid';
import { ModalControlService } from 'src/app/services/modal-control.service';
import { NavaidsCoChannelsService } from 'src/app/services/navaids-co-channels.service';
import { VicentyToolControlService } from 'src/app/services/vicenty-tool-control.service';
import * as olProj from 'ol/proj';
import olMap from 'ol/Map';
import { MapService } from 'src/app/services/map.service';
import { PointsService } from 'src/app/services/points.service';
import OlFeature from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import { FeatureType } from 'src/app/models/EnumTypes';
import { GlobalSelectionControlService } from 'src/app/map/services/global-selection-control.service';
import Point from 'ol/geom/Point';
import { Circle, Fill, Style } from 'ol/style';

@Component({
  selector: 'app-draw8db-boundary',
  templateUrl: './draw8db-boundary.component.html',
  styleUrls: ['../../app.component.scss'],
  standalone: false
})
export class Draw8dbBoundaryComponent implements OnInit {

  public desiredNavaid: NavaidModel;
  public undesiredNavaid: NavaidModel;
  public drawBoth: boolean = false;
  public NavaidsEIRP : number[];

  private map: olMap
  constructor(
    private modalService: ModalControlService,
    public bsModalRef : BsModalRef,
    public navaidsCoChannelService : NavaidsCoChannelsService,
    private vicentyService : VicentyToolControlService,
    private mapService: MapService,
    private pointsController: PointsService,
    private globalSelectionService: GlobalSelectionControlService
  ) { }

  ngOnInit(): void {
    this.map = this.mapService.getMap('map');
    this.activate8dbBoundaryLayer();
    this.NavaidsEIRP = [];
    this.desiredNavaid = this.navaidsCoChannelService.previousSelectedNavaids[0];
    this.desiredNavaid.declaredeirp != null ? this.NavaidsEIRP.push(this.desiredNavaid.declaredeirp) : false;
    this.undesiredNavaid = this.navaidsCoChannelService.previousSelectedNavaids[1];
    this.undesiredNavaid.declaredeirp != null ? this.NavaidsEIRP.push(this.undesiredNavaid.declaredeirp) : false;
  }

  private activate8dbBoundaryLayer(){
    this.globalSelectionService.dbBoundaryLayers.active = true;
     this.globalSelectionService.dbBoundaryLayers.childLayers?.forEach(el => {
       el.showAll();
       el.status = true;
     });
  }

  changeNavaidSelected() : void {
    this.navaidsCoChannelService.previousSelectedNavaids[0] == this.desiredNavaid ?
    this.undesiredNavaid = this.navaidsCoChannelService.previousSelectedNavaids[1] : this.undesiredNavaid = this.navaidsCoChannelService.previousSelectedNavaids[0];
  }

  // main functions ////////////////////////////////////////////////////////////////////////////////////
  performBoundary(){
    if (Math.abs(this.NavaidsEIRP[0] - this.NavaidsEIRP[1]) == 8) {
      this.modalService.showMesssageModal("Wrong EIRP value", "The power difference between the two station needs to be different than 8dB.");
      return;
    }
    const nav1 = cloneDeep(this.desiredNavaid);
    const nav2 = cloneDeep(this.undesiredNavaid);

    if (this.navaidsCoChannelService.previousSelectedNavaids[0] == this.desiredNavaid){
      nav1.declaredeirp = this.NavaidsEIRP[0];
      nav2.declaredeirp = this.NavaidsEIRP[1];
    } else {
      nav1.declaredeirp = this.NavaidsEIRP[1];
      nav2.declaredeirp = this.NavaidsEIRP[0];
    }
    // clear 8db Boundary tool layer
    this.navaidsCoChannelService.clear8dbDraw();

    this.calculateParameters(nav1, nav2);
    if (this.drawBoth) {
      this.calculateParameters(nav2, nav1);
    }

  }

  private calculateParameters(nav1: NavaidModel, nav2: NavaidModel ){
    const ratio: number = this.calculateRatio(nav1,nav2);
		const radius: number = Math.abs(this.calculateRadius(nav1, nav2, ratio));
		const centerOffset: number = this.calculateCenterOffset(nav1, nav2, ratio);

    const undesiredOnTheRight: boolean = nav2.longitude > nav1.longitude;

    const azimuth: number = VicentyToolControlService.calculateAzimuth(nav1.latitude, nav1.longitude, nav2.latitude, nav2.longitude);
		let newAzimuth: number = 0;

		if(undesiredOnTheRight){
			newAzimuth = azimuth+180;
		}
		else{
			newAzimuth = azimuth-180;
		}

    const centerPoint: number[] = VicentyToolControlService.calculateDestinationPoint(nav1.latitude, nav1.longitude, newAzimuth, centerOffset);
		let originCoord: Coordinate = [centerPoint[1], centerPoint[0]];

    const circleCoordinates: Coordinate[] = [];

		for(let i = 0; i<360; i ++){
			const coords: number[] = VicentyToolControlService.calculateDestinationPoint(originCoord[1], originCoord[0], i, radius);
			const coord: Coordinate = olProj.transform([coords[1], coords[0]],'EPSG:4326',this.map.getView().getProjection());
			circleCoordinates.push(coord);
		}

    originCoord = olProj.transform(originCoord,'EPSG:4326',this.map.getView().getProjection());

    this.drawCircle(circleCoordinates, originCoord);
  }
  //////////////////////////////////////////////////////////////////////////////////////////////////////

  // draw functions ////////////////////////////////////////////////////////////////////////////////////
  private drawCircle(circleCoordinates: Coordinate[], originCoord: Coordinate){
    // create features to add to layer
    const circle = new OlFeature({
      geometry: new Polygon([circleCoordinates]),
      featureType : FeatureType.DbBoundary
     });
     const point = new OlFeature({
      geometry: new Point(originCoord),
      featureType : FeatureType.DbBoundary
     })

    point.setStyle(new Style({
      image: new Circle({
          radius: 3,
          fill: new Fill({
              color: 'blue'
          })
      })
    }));

    // add features to layers
    let source = this.pointsController.dbBoundaryLayer.childLayers[0].layers
    .find((element) => element.id === '8db_boundary_layer')
    .olLayer.getSource();

    source.addFeature(circle);
    source.addFeature(point);
    this.cancel8dbBoundary();
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////


  // calculations functions /////////////////////////////////////////////////////////////////////////
  private calculateRatio(nav1: NavaidModel, nav2: NavaidModel) {
    //		R=10exp((EIRPd-EIRPu-DU)/20)
		const nav1Eirp: number = nav1.declaredeirp;
		const nav2Eirp: number = nav2.declaredeirp;
		const exp: number = (nav1Eirp-nav2Eirp-8)/20;
		return Math.pow(10, exp);
  }

  private calculateRadius(nav1: NavaidModel, nav2: NavaidModel, ratio: number){
    const deltaDistance: number = VicentyToolControlService.calculateDistance(nav1.latitude, nav1.longitude, nav2.latitude, nav2.longitude);
		const temp1: number = 1-Math.pow(ratio, 2);
		const temp2: number = ratio / temp1;
		return deltaDistance * temp2;
  }

  private calculateCenterOffset(nav1: NavaidModel, nav2: NavaidModel, ratio: number){
    const deltaDistance: number = VicentyToolControlService.calculateDistance(nav1.latitude, nav1.longitude, nav2.latitude, nav2.longitude);
		const temp1: number = 1-Math.pow(ratio, 2);
		const temp2: number = Math.pow(ratio, 2) / temp1;
		return deltaDistance * temp2;
  }
  ///////////////////////////////////////////////////////////////////////////////////////////

  // close functions /////////////////////////////////////////////////////////////////////////

  cancel8dbBoundary():void {
    this.bsModalRef.hide();
  }
  ///////////////////////////////////////////////////////////////////////////////////////////



}
